import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow } from 'flowbite-react';
import Modals from '../../components/Modal';
import FormProject from './FormProject';
import LoadingSpinner from '../../components/LoadingSpinner';

const Projects = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareProject, setShareProject] = useState(null);
  const [projectDeliverables, setProjectDeliverables] = useState([]);
  const [shareError, setShareError] = useState(null);

  const openModal = (project = null) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/projects`,
        { withCredentials: true },
      );
      setProjects(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleSubmitProject = async (data) => {
    setIsLoading(true);
    try {
      if (editingProject) {
        const response = await axios.put(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/project/${editingProject.id}/update`,
          data,
          { withCredentials: true },
        );
        setProjects(
          projects.map((p) =>
            p.id === editingProject.id ? response.data.project : p,
          ),
        );
      } else {
        const response = await axios.post(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/project/create`,
          data,
          { withCredentials: true },
        );
        setProjects([response.data.project, ...projects]);
      }
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
      alert('Error al guardar proyecto');
    } finally {
      setIsLoading(false);
    }
  };

  const openShareModal = async (project) => {
    setShareProject(project);
    setIsShareModalOpen(true);
    setShareError(null);
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/project/${project.id}/deliverables`,
        { withCredentials: true },
      );
      setProjectDeliverables(response.data);
    } catch (err) {
      setShareError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setShareProject(null);
    setProjectDeliverables([]);
    setShareError(null);
  };

  return (
    <div className="p-4 mt-4">
      {isLoading && <LoadingSpinner message="Procesando..." size="lg" color="blue" />}

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Proyectos</h1>
        <Button color="gray" onClick={() => openModal(null)}>
          Nuevo Proyecto
        </Button>
      </div>

      <p className="text-gray-600 mb-4">
        Módulo de gestión documental - Proyectos.
      </p>
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col overflow-hidden"
          >
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-3 flex items-center justify-between">
              <div className="flex flex-col overflow-hidden">
                <span className="text-[10px] uppercase tracking-wide text-sky-100">
                  Cliente: <span className="text-sm font-semibold truncate">
                            {project.Client?.business_name || 'Sin cliente'}
                          </span>
                </span>
                
                {project.Client?.rut && (
                  <span className="text-[11px] text-sky-100 mt-0.5">
                    RUT: <span className="text-sm font-semibold truncate">{project.Client.rut}</span>
                  </span>
                )}
              </div>
              <Badge
                color="info"
                className="bg-white/10 border border-white/30 text-[11px] px-2 py-0.5 rounded-full"
              >
                {project.project_type}
              </Badge>
            </div>

            <div className="p-4 space-y-2 text-sm text-gray-700">
              <p>
                <span className="font-semibold">Área: </span>
                <span>{project.area}</span>
              </p>
              <p>
                <span className="font-semibold">Inicio: </span>
                <span>
                  {project.start_date
                    ? new Date(project.start_date).toLocaleDateString()
                    : 'N/D'}
                </span>
              </p>
              <p>
                <span className="font-semibold">Go Live: </span>
                <span>
                  {project.go_live_date
                    ? new Date(project.go_live_date).toLocaleDateString()
                    : 'Pendiente'}
                </span>
              </p>
              <p>
                <span className="font-semibold">Cierre: </span>
                <span>
                  {project.end_date
                    ? new Date(project.end_date).toLocaleDateString()
                    : 'Abierto'}
                </span>
              </p>
            </div>

            <div className="px-4 pb-4 pt-1 flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  size="xs"
                  color="light"
                  className="border border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => navigate(`/app/proyecto/${project.id}`)}
                >
                  Ver detalle
                </Button>
                <Button
                  size="xs"
                  color="light"
                  className="border border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => openShareModal(project)}
                >
                  Compartir entregables
                </Button>
                <Button
                  size="xs"
                  color="light"
                  className="border border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => openModal(project)}
                >
                  Editar
                </Button>
              </div>
            </div>
          </div>
        ))}
        {projects.length === 0 && !isLoading && (
          <div className="text-gray-500 col-span-full">
            No hay proyectos creados.
          </div>
        )}
      </div>

      <Modals
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
        modalSize="3xl"
      >
        <FormProject onSubmit={handleSubmitProject} onClose={closeModal} project={editingProject} />
      </Modals>

      <Modals
        isOpen={isShareModalOpen}
        onClose={closeShareModal}
        title={shareProject ? `Entregables de ${shareProject.Client?.business_name || 'Proyecto'}` : 'Entregables del proyecto'}
        modalSize="4xl"
      >
        {shareError && <div className="text-red-500 mb-2">{shareError}</div>}
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableHeadCell>Entregable</TableHeadCell>
              <TableHeadCell>Tipo proyecto</TableHeadCell>
              <TableHeadCell>
                <span className="srOnly">Acciones</span>
              </TableHeadCell>
            </TableHead>
            <TableBody className="divide-y">
              {projectDeliverables.map((pd) => (
                <TableRow key={pd.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {pd.deliverable?.name}
                  </TableCell>
                  <TableCell>{pd.project_type}</TableCell>
                  <TableCell className="inline-flex">
                    <Button
                      size="xs"
                      color="light"
                      className="border border-gray-300 text-gray-700 hover:bg-gray-100"
                      onClick={async () => {
                        try {
                          const response = await axios.post(
                            `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverable/${pd.deliverable_id}/generate-link`,
                            { projectId: shareProject.id },
                            { withCredentials: true },
                          );
                          const token = response.data.token;
                          const base = (import.meta.env.VITE_ENV == 'development')
                            ? import.meta.env.VITE_BACKEND_LOCAL
                            : import.meta.env.VITE_BACKEND;
                          if (token && base) {
                            const backendBase = base.replace(/\/$/, '');
                            const liveLink = `${backendBase}/public/deliverable/${token}`;
                            window.open(liveLink, '_blank', 'noopener,noreferrer');
                          } else {
                            alert('No se pudo obtener el enlace del entregable');
                          }
                        } catch (err) {
                          console.error('Error al generar enlace:', err);
                          alert('Error al generar enlace');
                        }
                      }}
                    >
                      Compartir enlace
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {projectDeliverables.length === 0 && (
                <TableRow>
                  <TableCell colSpan="3" className="text-center text-gray-500 py-4">
                    No hay entregables asociados a este proyecto.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Modals>
    </div>
  );
};

export default Projects;
